import React, { useMemo, useState } from 'react';
import {
  Mail,
  Send,
  Loader2,
  CheckCircle,
  User,
  MessageSquare,
  ShieldCheck,
  Clock,
  HelpCircle,
  ArrowRight,
  ExternalLink,
  AlertCircle,
  Brain,
  FileText,
  Target,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { PageLayout } from '../components/Layout';
import { SEO, PageSEO } from '../components/SEO';
import { apiClient } from '../lib/api';
import { toast } from 'sonner';

const CONTACT_EMAIL = 'trainingteam@traindriverfoundation.com';

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0 },
};

const supportCards = [
  {
    icon: Brain,
    title: 'Psychometric practice support',
    text: 'Questions about Group Bourdon, TEA-Occ, WAFV, ATAVT, TRP, 2HAND or your practice dashboard.',
  },
  {
    icon: FileText,
    title: 'Account or access issues',
    text: 'Help with logging in, purchases, access, progress tracking or technical issues on the platform.',
  },
  {
    icon: Target,
    title: 'Assessment preparation guidance',
    text: 'General guidance on preparing for train driver selection, including MMI and SJE preparation routes.',
  },
];

const quickLinks = [
  {
    label: 'View pricing',
    href: '/pricing',
  },
  {
    label: 'Psychometric tests guide',
    href: '/tests',
  },
  {
    label: 'Frequently asked questions',
    href: '/faq',
  },
];

export const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });

  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const characterCount = formData.message.length;

  const isFormValid = useMemo(() => {
    return (
      formData.name.trim().length > 1 &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim()) &&
      formData.message.trim().length >= 10
    );
  }, [formData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const getErrorMessage = (error) => {
    const detail = error?.response?.data?.detail;

    if (typeof detail === 'string') return detail;

    if (Array.isArray(detail) && detail.length > 0) {
      return detail[0]?.msg || 'Failed to send message. Please try again.';
    }

    return 'Failed to send message. Please try again.';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      toast.error('Please enter a valid email address');
      return;
    }

    if (formData.message.trim().length < 10) {
      toast.error('Please enter a message with at least 10 characters');
      return;
    }

    setLoading(true);

    try {
      await apiClient.sendContactMessage({
        name: formData.name.trim(),
        email: formData.email.trim(),
        message: formData.message.trim(),
      });

      setSubmitted(true);
      toast.success('Message sent successfully');
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setSubmitted(false);
    setFormData({ name: '', email: '', message: '' });
  };

  return (
    <PageLayout>
      <SEO {...PageSEO.contact} />

      <main className="min-h-screen bg-[#F8FAFC]">
        {/* Hero */}
        <section className="relative overflow-hidden bg-[#0B1D51]">
          <div className="absolute inset-0">
            <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-[#FF9F1C]/20 blur-3xl" />
            <div className="absolute bottom-0 left-0 h-80 w-80 rounded-full bg-blue-400/10 blur-3xl" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,159,28,0.18),transparent_35%),linear-gradient(135deg,#0B1D51_0%,#071331_100%)]" />
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-24">
            <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-10 items-center">
              <motion.div
                initial="hidden"
                animate="visible"
                variants={fadeUp}
                transition={{ duration: 0.5 }}
              >
                <Badge className="mb-5 bg-[#FF9F1C] hover:bg-[#FF9F1C] text-white border-0 shadow-sm">
                  {submitted ? 'Message received' : 'Contact TDF Psychometrics'}
                </Badge>

                <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-tight mb-5">
                  {submitted ? 'Your message has been sent' : 'Need help with train driver psychometric preparation?'}
                </h1>

                <p className="text-lg sm:text-xl text-white/78 max-w-2xl leading-relaxed">
                  Contact the Train Driver Foundation Psychometrics team for support with your account,
                  practice tests, access, or preparation route.
                </p>

                <div className="mt-8 grid sm:grid-cols-3 gap-3 max-w-3xl">
                  {[
                    { icon: Clock, label: 'Support response', value: 'As soon as possible' },
                    { icon: ShieldCheck, label: 'Rail-focused', value: 'UK train driver prep' },
                    { icon: HelpCircle, label: 'Best for', value: 'Access, tests & guidance' },
                  ].map((item) => {
                    const Icon = item.icon;
                    return (
                      <div
                        key={item.label}
                        className="rounded-2xl bg-white/10 border border-white/15 p-4 backdrop-blur"
                      >
                        <Icon className="w-5 h-5 text-[#FF9F1C] mb-3" />
                        <p className="text-xs uppercase tracking-wide text-white/50 font-semibold">
                          {item.label}
                        </p>
                        <p className="text-sm text-white font-semibold mt-1">
                          {item.value}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </motion.div>

              <motion.div
                initial="hidden"
                animate="visible"
                variants={fadeUp}
                transition={{ duration: 0.5, delay: 0.12 }}
                className="hidden lg:block"
              >
                <div className="rounded-[2rem] border border-white/15 bg-white/10 p-6 shadow-2xl backdrop-blur">
                  <div className="rounded-3xl bg-white p-6">
                    <div className="flex items-center gap-3 mb-5">
                      <div className="w-12 h-12 rounded-2xl bg-[#0B1D51] flex items-center justify-center">
                        <Mail className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <p className="font-heading font-bold text-[#0B1D51] text-lg">
                          What to include
                        </p>
                        <p className="text-sm text-slate-500">
                          Help us route your query quickly
                        </p>
                      </div>
                    </div>

                    <div className="space-y-3">
                      {[
                        'The email address used for your account',
                        'Which test, product or page you need help with',
                        'A short description of the issue or question',
                      ].map((text, index) => (
                        <div key={text} className="flex items-start gap-3 rounded-2xl bg-slate-50 p-4">
                          <span className="w-6 h-6 rounded-full bg-[#FF9F1C]/15 text-[#C77700] flex items-center justify-center text-xs font-bold shrink-0">
                            {index + 1}
                          </span>
                          <p className="text-sm text-slate-700 leading-relaxed">
                            {text}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Main Contact Section */}
        <section className="py-12 sm:py-16 lg:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-[0.95fr_1.05fr] gap-8 lg:gap-10 items-start">
              {/* Left content */}
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: '-80px' }}
                variants={fadeUp}
                transition={{ duration: 0.45 }}
                className="space-y-6"
              >
                <div>
                  <Badge className="mb-4 bg-[#0B1D51]/10 text-[#0B1D51] hover:bg-[#0B1D51]/10 border-0">
                    How we can help
                  </Badge>

                  <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#0B1D51] tracking-tight mb-4">
                    Get the right support without digging around the site
                  </h2>

                  <p className="text-slate-600 leading-relaxed">
                    Whether you are preparing for a UK train driver assessment process,
                    need technical support, or want to check which preparation resource is
                    right for you, send us the details and we will point you in the right direction.
                  </p>
                </div>

                <div className="grid gap-4">
                  {supportCards.map((card) => {
                    const Icon = card.icon;
                    return (
                      <Card key={card.title} className="border-slate-200 shadow-sm rounded-2xl">
                        <CardContent className="p-5 flex gap-4">
                          <div className="w-11 h-11 rounded-2xl bg-[#FF9F1C]/12 flex items-center justify-center shrink-0">
                            <Icon className="w-5 h-5 text-[#C77700]" />
                          </div>
                          <div>
                            <h3 className="font-heading font-bold text-[#0B1D51] mb-1">
                              {card.title}
                            </h3>
                            <p className="text-sm text-slate-600 leading-relaxed">
                              {card.text}
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>

                <Card className="border-amber-200 bg-amber-50 rounded-2xl shadow-sm">
                  <CardContent className="p-5 flex gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-amber-950 mb-1">
                        Important assessment note
                      </p>
                      <p className="text-sm text-amber-900/80 leading-relaxed">
                        TDF provides preparation and practice support. We do not administer official
                        train driver psychometric assessments or guarantee recruitment outcomes.
                      </p>
                    </div>
                  </CardContent>
                </Card>

                <div className="rounded-2xl bg-white border border-slate-200 p-5 shadow-sm">
                  <p className="font-heading font-bold text-[#0B1D51] mb-3">
                    Popular pages
                  </p>
                  <div className="grid sm:grid-cols-3 gap-3">
                    {quickLinks.map((link) => (
                      <Link
                        key={link.href}
                        to={link.href}
                        className="group rounded-xl border border-slate-200 p-3 text-sm font-semibold text-slate-700 hover:border-[#FF9F1C] hover:text-[#0B1D51] transition-colors"
                      >
                        <span className="flex items-center justify-between gap-2">
                          {link.label}
                          <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-[#FF9F1C]" />
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              </motion.div>

              {/* Form / Success */}
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: '-80px' }}
                variants={fadeUp}
                transition={{ duration: 0.45, delay: 0.08 }}
              >
                {submitted ? (
                  <Card className="border-slate-200 shadow-xl rounded-3xl overflow-hidden">
                    <CardContent className="p-8 sm:p-10 text-center">
                      <div className="w-20 h-20 mx-auto mb-6 bg-[#2CB67D]/10 rounded-full flex items-center justify-center">
                        <CheckCircle className="w-10 h-10 text-[#2CB67D]" />
                      </div>

                      <h2 className="font-heading text-3xl font-bold text-[#0B1D51] mb-3">
                        Thank you
                      </h2>

                      <p className="text-slate-600 leading-relaxed max-w-md mx-auto mb-8">
                        Your message has been sent to the Train Driver Foundation team.
                        We will get back to you as soon as possible.
                      </p>

                      <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        <Button
                          onClick={handleReset}
                          variant="outline"
                          className="rounded-xl"
                          data-testid="send-another-btn"
                        >
                          Send another message
                        </Button>

                        <Button
                          asChild
                          className="rounded-xl bg-[#0B1D51] hover:bg-[#071331] text-white"
                        >
                          <Link to="/pricing">
                            View preparation options
                            <ArrowRight className="w-4 h-4 ml-2" />
                          </Link>
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  <Card className="border-slate-200 shadow-xl rounded-3xl overflow-hidden">
                    <div className="bg-white border-b border-slate-200 p-6 sm:p-8">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-2xl bg-[#0B1D51] flex items-center justify-center shrink-0">
                          <Mail className="w-6 h-6 text-white" />
                        </div>

                        <div>
                          <h2 className="font-heading text-2xl font-bold text-[#0B1D51]">
                            Send us a message
                          </h2>
                          <p className="text-slate-600 mt-1 leading-relaxed">
                            Give us enough detail to identify your account, product or preparation question.
                          </p>
                        </div>
                      </div>
                    </div>

                    <CardContent className="p-6 sm:p-8 bg-white">
                      <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="grid sm:grid-cols-2 gap-5">
                          <div className="space-y-2">
                            <label
                              htmlFor="name"
                              className="text-sm font-semibold text-[#0B1D51] flex items-center gap-2"
                            >
                              <User className="w-4 h-4 text-slate-500" />
                              Your name
                            </label>

                            <Input
                              id="name"
                              name="name"
                              type="text"
                              placeholder="John Smith"
                              value={formData.name}
                              onChange={handleChange}
                              className="h-12 rounded-xl border-slate-200 focus:border-[#0B1D51] focus:ring-[#0B1D51]"
                              data-testid="contact-name-input"
                              autoComplete="name"
                              required
                            />
                          </div>

                          <div className="space-y-2">
                            <label
                              htmlFor="email"
                              className="text-sm font-semibold text-[#0B1D51] flex items-center gap-2"
                            >
                              <Mail className="w-4 h-4 text-slate-500" />
                              Email address
                            </label>

                            <Input
                              id="email"
                              name="email"
                              type="email"
                              placeholder="john@example.com"
                              value={formData.email}
                              onChange={handleChange}
                              className="h-12 rounded-xl border-slate-200 focus:border-[#0B1D51] focus:ring-[#0B1D51]"
                              data-testid="contact-email-input"
                              autoComplete="email"
                              required
                            />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <div className="flex items-center justify-between gap-3">
                            <label
                              htmlFor="message"
                              className="text-sm font-semibold text-[#0B1D51] flex items-center gap-2"
                            >
                              <MessageSquare className="w-4 h-4 text-slate-500" />
                              Message
                            </label>

                            <span className={`text-xs ${characterCount < 10 ? 'text-slate-400' : 'text-[#2CB67D]'}`}>
                              {characterCount} characters
                            </span>
                          </div>

                          <Textarea
                            id="message"
                            name="message"
                            placeholder="Please include your question, the product or test you need help with, and the email address used for your account if different."
                            value={formData.message}
                            onChange={handleChange}
                            rows={7}
                            className="rounded-xl border-slate-200 focus:border-[#0B1D51] focus:ring-[#0B1D51] resize-none leading-relaxed"
                            data-testid="contact-message-input"
                            required
                          />
                        </div>

                        <Button
                          type="submit"
                          className="w-full h-12 rounded-xl bg-[#FF9F1C] hover:bg-[#E88900] text-white font-bold shadow-lg shadow-amber-500/20 disabled:opacity-60"
                          disabled={loading || !isFormValid}
                          data-testid="contact-submit-btn"
                        >
                          {loading ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Sending message...
                            </>
                          ) : (
                            <>
                              <Send className="w-4 h-4 mr-2" />
                              Send message
                            </>
                          )}
                        </Button>
                      </form>

                      <div className="mt-8 pt-6 border-t border-slate-200">
  <div className="rounded-2xl bg-slate-50 p-4">
    <div className="flex items-center justify-center gap-3">
      <p className="text-sm text-slate-600">
        Prefer direct email?
      </p>

      <a
        href={`mailto:${CONTACT_EMAIL}`}
        className="group inline-flex items-center justify-center w-11 h-11 rounded-xl bg-[#0B1D51] text-white hover:bg-[#FF9F1C] transition-colors shadow-sm"
        aria-label="Email TDF Psychometrics"
        title="Email TDF Psychometrics"
      >
        <Mail className="w-5 h-5" />
      </a>
    </div>
  </div>
</div>
                    </CardContent>
                  </Card>
                )}
              </motion.div>
            </div>
          </div>
        </section>
      </main>
    </PageLayout>
  );
};

export default ContactPage;